// components/WelcomeScreen.tsx
import { Box, Typography } from '@mui/material';
import { ReactComponent as DarkModeLogo } from 'assets/logos/symbot-icon-blue.svg';
import { useCurrentUserInfo } from 'hooks/useCurrentUserInfo';
import { SuggestionsCarousel } from './SuggestionsCarousel';
import { useChatbotContext } from '../context/ChatbotContext';
import useAppTheme from 'hooks/useAppTheme';
import { ThemePalette } from 'mui.theme';
import {
  CLIENT_THEME,
  clientThemePalette,
  getClientLogoSmall,
} from 'utils/clientTheme';
import { useTranslation } from 'react-i18next';

interface WelcomeScreenProps {
  onSelect: (question: string) => void;
  predefinedQuestions: string[];
  showPersistentSuggestions: boolean;
}

export const WelcomeScreen = ({
  onSelect,
  predefinedQuestions,
  showPersistentSuggestions,
}: WelcomeScreenProps) => {
  const { t } = useTranslation();
  const currentUser = useCurrentUserInfo();
  const { enableDarkTheme } = useAppTheme();
  const { isExpanded } = useChatbotContext();

  const colorMode = enableDarkTheme ? 'dark' : 'light';
  const themeColors = clientThemePalette[CLIENT_THEME][colorMode];
  const accentColor = themeColors.primary;
  const clientLogo = getClientLogoSmall();

  const logoSize = isExpanded ? 70 : 58;
  const innerSize = isExpanded ? 48 : 38;

  return (
    <Box
      display="flex"
      alignItems="center"
      flexDirection="column"
      width="100%"
      gap={isExpanded ? 2 : 1}
      justifyContent="center"
      height="100%"
      sx={{ px: 2 }}
    >
      {/* Logo container with glow ring */}
      <Box
        sx={{
          position: 'relative',
          width: logoSize,
          height: logoSize,
          flexShrink: 0,
          mb: 0.5,
        }}
      >
        {/* Outer glow ring */}
        <Box
          sx={{
            position: 'absolute',
            inset: -4,
            borderRadius: '28px 28px 6px 28px',
            background: `radial-gradient(circle, ${accentColor}30, transparent 70%)`,
            pointerEvents: 'none',
          }}
        />
        {/* Icon box */}
        <Box
          sx={{
            width: '100%',
            height: '100%',
            borderRadius: '22px 22px 4px 22px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background:
              clientLogo.type === 'img'
                ? `linear-gradient(145deg, ${accentColor}dd, ${accentColor}99)`
                : `linear-gradient(145deg, #1049E5, #3b6cf7)`,
            border: `1.5px solid ${accentColor}55`,
            boxShadow: `0 6px 20px ${accentColor}44`,
          }}
        >
          <Box
            sx={{
              width: innerSize,
              height: innerSize,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {clientLogo.type === 'img' ? (
              <img
                src={clientLogo.src}
                alt={clientLogo.alt}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  filter: 'brightness(0) invert(1)',
                }}
              />
            ) : (
              <DarkModeLogo />
            )}
          </Box>
        </Box>
      </Box>

      {/* Greeting */}
      <Typography
        sx={{
          fontSize: isExpanded ? '1.75rem' : '1.35rem',
          fontWeight: 700,
          mt: 0.5,
          mb: 0,
          textAlign: 'center',
          color: enableDarkTheme ? '#F5F7FF' : ThemePalette.typography.darkGrey,
          lineHeight: 1.2,
        }}
      >
        {t('hello')}{' '}
        <Box component="span" sx={{ color: accentColor }}>
          {currentUser?.firstName?.trim() ?? 'there'}
        </Box>{' '}
        👋
      </Typography>

      {/* Subtitle */}
      <Typography
        sx={{
          color: enableDarkTheme ? '#8a9ab0' : ThemePalette.typography.grey,
          mx: isExpanded ? 14 : 4,
          fontSize: isExpanded ? '0.95rem' : '0.82rem',
          textAlign: 'center',
          lineHeight: 1.6,
          mt: 0.25,
        }}
      >
        {t('mining_operations_at_your_fingertips_ask_me_anything_to_chec')}
      </Typography>

      {/* Divider accent */}
      <Box
        sx={{
          width: 40,
          height: 3,
          borderRadius: 2,
          background: `linear-gradient(90deg, ${accentColor}, ${accentColor}44)`,
          my: 0.5,
        }}
      />

      {/* Suggestions */}
      {showPersistentSuggestions && (
        <Box width="100%">
          <SuggestionsCarousel
            suggestions={predefinedQuestions}
            onSelect={onSelect}
            isInitial={true}
          />
        </Box>
      )}
    </Box>
  );
};
